import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, Box, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { omit } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useFormContext, useWatch } from 'react-hook-form';
import useSWR, { type Fetcher, useSWRConfig } from 'swr';

import { useAnalytics } from '~/analytics';
import { UserContext } from '~/auth/userContext';
import LAIConfirmationDialog from '~/components/dialogs/LAIConfirmationDialog';
import {
  createFormInvalidHandler,
  FieldAndCell,
  formatLiftAPIClientError,
} from '~/components/form/utils';
import CreditFields from '~/components/invoices/CreditFields';
import { buildFieldDef } from '~/components/invoices/invoiceFields';
import type {
  LAIUseFormReturn,
  LiftAIDocumentTagged,
  LiftAIFormProps,
} from '~/components/uploadDocument/SelectDocumentTypeDialog';
import {
  associateFileToEntity,
  getFilesAssociatedToEntity,
  updateAssociationFileToEntity,
} from '~/components/utils/upload';
import { consultantsRetrieve } from '~/data/generated/endpoints/consultants/consultants';
import {
  invoicesCreate,
  invoicesList,
  invoicesPartialUpdate,
} from '~/data/generated/endpoints/invoices/invoices';
import { propertiesFormList } from '~/data/generated/endpoints/properties/properties';
import { serviceProvidersList } from '~/data/generated/endpoints/service-providers/service-providers';
import type { PropertyForm, ServiceProviderGroup } from '~/data/generated/model';
import { InvoicesListKindItem } from '~/data/generated/model';
import { creditLinksMutator, getInvoiceMutator } from '~/data/hooks/utils';
import { LiftAPIClientError } from '~/data/liftApiMutator';
import { parseApiResponse } from '~/data/parseApiResponse';
import type {
  API_InvoiceCreateRequest,
  Invoice,
  InvoiceForm as InvoiceDto,
  InvoiceFormOption,
  InvoiceFormValues as InvoiceDtoValues,
} from '~/data/schemas/invoice';
import {
  invoiceFormOptionSchema,
  invoiceFormSchema,
  InvoiceKind,
  invoiceKindToLabelMap,
  InvoiceStatus,
  InvoiceType,
} from '~/data/schemas/invoice';
import type { API_UserGroup, UserDisplay } from '~/data/schemas/user';
import { userDisplaySchema } from '~/data/schemas/user';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { usePropertyCars } from '~/hooks/usePropertyCars';
import { toDateLocal } from '~/utils/format';

const invoiceOptionsFetcher: Fetcher<
  {
    consultants: UserDisplay[];
    properties: PropertyForm[];
  },
  readonly [key: string, userGroup: API_UserGroup]
> = async ([_, userGroup]) => {
  const [{ data: consultantGroup }, propertiesResponse] = await Promise.all([
    consultantsRetrieve(userGroup.id),
    propertiesFormList(),
  ]);

  return {
    // The consultants endpoint (ConsultantGroupSerializer.get_consultants →
    // ConsultantSerializer) emits the restricted user shape — id/username/
    // full_name/email/phone_number only, no role/isLiftaiAdmin — so parse
    // against the matching display schema. Parsing against the full
    // `api_userSchema` threw for every consultant and broke invoice creation
    // (AMP-585).
    consultants: parseApiResponse(
      userDisplaySchema.array(),
      consultantGroup.consultants ?? [],
      'ConsultantListItem',
    ),
    properties: propertiesResponse.data,
  };
};

const invoiceServiceProvidersFetcher: Fetcher<
  ServiceProviderGroup[],
  readonly [key: 'invoiceServiceProviders', propertyId: string]
> = async ([_, propertyId]) => {
  const { data } = await serviceProvidersList({ property_id: propertyId });
  return data;
};

const originalInvoiceOptionsFetcher: Fetcher<
  InvoiceFormOption[],
  readonly [key: 'invoiceAdjustmentOriginals', propertyId: string, serviceProviderId: string]
> = async ([_, propertyId, serviceProviderId]) => {
  const { data } = await invoicesList({
    property: propertyId,
    service_provider: serviceProviderId ? [serviceProviderId] : undefined,
    kind: [InvoicesListKindItem.invoice],
    // The API refuses to link to an adjustment, so none is offered as an original.
    is_adjustment: false,
    ordering: 'number',
    variant: 'form',
  });
  // variant=form is unpaginated today; normalize the envelope shape anyway.
  const results = Array.isArray(data) ? data : data.results;
  return parseApiResponse(invoiceFormOptionSchema.array(), results, 'InvoiceFormOption');
};

export const getDefaultInvoiceFormValues = (): InvoiceDto => {
  return {
    id: '',
    number: '',
    date: '',
    kind: InvoiceKind.Invoice,
    type: InvoiceType.Maintenance,
    status: InvoiceStatus.Pending,
    propertyId: '',
    carIds: [],
    consultantId: '',
    lineItems: [],
    proposedAmount: 0,
    description: undefined,
    autoUpload: false,
    serviceProviderId: '',
    stampFile: null,
    creditedInvoiceId: null,
    creditTarget: 'account',
    keepOnFileOnly: false,
  };
};

export const mapInvoiceToFormValues = (invoice: Invoice): InvoiceDto => {
  const form: InvoiceDto = {
    ...getDefaultInvoiceFormValues(),
    id: invoice.id,
    propertyId: invoice.property?.id ?? undefined,
    consultantId: invoice.consultant?.id ?? '',
    serviceProviderId: invoice.serviceProvider?.id ?? '',
    carIds: invoice.carsServiced?.map((car) => car.id) ?? [],
    kind: invoice.kind,
    date: invoice.date ? toDateLocal(invoice.date) : '',
    number: invoice.number,
    type: invoice.type,
    status: invoice.status,
    description: invoice.description,
    proposedAmount: invoice.proposedAmount,
    lineItems: invoice.lineItems,
    autoUpload: invoice.autoUpload,
    creditedInvoiceId: invoice.creditedInvoice?.id ?? null,
    creditTarget: invoice.creditedInvoice ? 'invoice' : 'account',
    keepOnFileOnly: invoice.keepOnFileOnly ?? false,
  };
  return form;
};

export const applyCreditRules = (invoiceData: InvoiceDto): InvoiceDto => {
  if (invoiceData.kind !== InvoiceKind.Credit) {
    return omit(invoiceData, ['creditedInvoiceId', 'creditTarget', 'keepOnFileOnly']);
  }
  // Credits are approved when filed and take their type from what they apply to.
  return {
    ...omit(invoiceData, ['type', 'carIds', 'creditTarget']),
    status: InvoiceStatus.Approved,
    creditedInvoiceId:
      invoiceData.creditTarget === 'account' ? null : (invoiceData.creditedInvoiceId ?? null),
    keepOnFileOnly: invoiceData.keepOnFileOnly ?? false,
  } as InvoiceDto;
};

/** API Create Request */
const saveInvoice = async (dto: InvoiceDto, confirmDuplicate = false): Promise<Invoice> => {
  // Update is a PATCH (invoicesPartialUpdate); create is a POST. The form still
  // works in terms of the hand-written API_Invoice{Update,Create}Request
  // (camelCase payloads unchanged), tightened relative to the generated body, so
  // cast at this boundary (AMP-503).
  const { data: invoice } = await (dto.id
    ? invoicesPartialUpdate(
        dto.id,
        omit(dto, ['id', 'attachments']) as unknown as Parameters<typeof invoicesPartialUpdate>[1],
      )
    : invoicesCreate({
        ...(omit(dto, ['id', 'attachments']) as API_InvoiceCreateRequest),
        ...(confirmDuplicate ? { confirmDuplicate: true } : {}),
      } as unknown as Parameters<typeof invoicesCreate>[0]));

  // The create/update response is the (sparser) write serializer, not the full
  // read Invoice, so it can't be parsed against invoiceSchema; cast here.
  return invoice as unknown as Invoice;
};

const updateReferenceToFiles = async (invoice: Invoice, document: LiftAIDocumentTagged) => {
  if (invoice.id) {
    const attachments = await getFilesAssociatedToEntity(invoice.id, 'invoice');

    await Promise.all(
      attachments
        .filter((attachment) => attachment.isPrimary)
        .map((attachment) =>
          updateAssociationFileToEntity(attachment.attachment, invoice.id, 'invoice', {
            id: attachment.id,
            isVoid: true,
          }),
        ),
    );
  }

  await associateFileToEntity(
    document.attachment.id,
    invoice.id,
    'invoice',
    document.fileType === 'pdf'
      ? {
          pageNumber: document.pageNum,
          isPrimary: true,
        }
      : {},
  );
};

export const useInvoiceForm = ({ showKind = false } = {}): LAIUseFormReturn<
  InvoiceDtoValues,
  InvoiceDto
> & {
  Fields: typeof InvoiceFields;
} => {
  const hookForm = useForm<InvoiceDtoValues, unknown, InvoiceDto>({
    mode: 'all',
    defaultValues: getDefaultInvoiceFormValues(),
    resolver: zodResolver(invoiceFormSchema),
  });

  return {
    hookForm,
    Form: InvoiceForm,
    Fields: showKind ? InvoiceFieldsWithKind : InvoiceFields,
  };
};

const InvoiceForm = ({
  children,
  document,
  onSubmit,
  initialData,
}: LiftAIFormProps<InvoiceDtoValues, Invoice>) => {
  const { mutate } = useSWRConfig();
  const { showEntityActionSnackbar, showSnackbar } = useLAISnackbar();
  const { handleSubmit, subscribe, reset, resetField, trigger, setValue } = useFormContext<
    InvoiceDtoValues,
    unknown,
    InvoiceDto
  >();
  const [pendingInvoiceData, setPendingInvoiceData] = useState<InvoiceDto | null>(null);
  const { track } = useAnalytics();

  useEffect(() => {
    if (!initialData) {
      return;
    }

    const formValues = {
      ...getDefaultInvoiceFormValues(),
      ...initialData,
    };

    reset(formValues);
  }, [initialData, reset]);

  useEffect(() => {
    // Only clear lifts on user-driven property changes. `type === 'change'`
    // fires for the registered field's onChange (via Controller / autocomplete);
    // `setValue`, `reset`, and initial hydration produce `type === undefined`.
    return subscribe({
      formState: { values: true },
      callback: ({ name, type }) => {
        if (name === 'propertyId' && type === 'change') {
          resetField('carIds', { defaultValue: [] });
          // The credit picker only offers invoices on the chosen property, and
          // the API rejects a cross-property link.
          resetField('creditedInvoiceId', { defaultValue: null });
        }
      },
    });
  }, [subscribe, resetField]);

  useEffect(() => {
    // This cascade depends on VALUE-CHANGE notifications. From 7.85 onward
    // react-hook-form skips the notification when `setValue` is handed the value
    // a field already holds, and for a field-array root like `lineItems` the
    // skip emits nothing at all. The live typing path reaches this through
    // `Controller.onChange`, which always carries a real change, so the cascade
    // fires today. Do not reroute writes to `lineItems` or
    // `lineItems.N.amount` through `setValue` without re-checking that the
    // proposed-amount total still recomputes.
    return subscribe({
      formState: { values: true },
      callback: ({ values: { lineItems, proposedAmount }, name }) => {
        if (lineItems && name?.match(/lineItems\.\d+\.amount/)) {
          const lineItemsTotal = lineItems
            // Even though the types do not express this, `amount` comes from the
            // form field as a string (and can be the empty string), so coerce each
            // value to a number before summing — otherwise `acc + curr` would
            // concatenate strings and `lineItemsTotal.toFixed` would throw.
            .map((item) => Number(item?.amount) || 0)
            .reduce((acc, curr) => acc + curr, 0);

          if (Number(proposedAmount) !== lineItemsTotal) {
            setValue('proposedAmount', parseFloat(lineItemsTotal.toFixed(2)));
          }
        }
      },
    });
  }, [subscribe, setValue, trigger]);

  const finishSave = useCallback(
    async (invoiceData: InvoiceDto, confirmDuplicate = false) => {
      const isEdit = Boolean(invoiceData.id);
      const invoice = await saveInvoice(invoiceData, confirmDuplicate);

      if (document) {
        await updateReferenceToFiles(invoice, document);
      }

      // Edits land here too — `saveInvoice` routes to `update` when
      // dto.id is set. Track only edits (not creates) since the
      // creation path is already represented by the document_review
      // → approve → invoice flow elsewhere, while edit is the
      // unapprove-and-amend loop.
      if (isEdit && invoice.id) {
        track('invoice_action_taken', { invoice_id: invoice.id, action: 'edit' });
      }

      onSubmit(invoice);

      const action = invoiceData.id ? ActionEnum.Update : ActionEnum.Create;
      showEntityActionSnackbar(
        {
          name: invoiceKindToLabelMap.get(invoiceData.kind)!,
          action,
          id: invoiceData.number,
        },
        { variant: 'info' },
      );

      void mutate(getInvoiceMutator(invoiceData.id));
      if (invoiceData.kind === InvoiceKind.Credit) {
        void mutate(creditLinksMutator);
      }
    },
    [document, onSubmit, showEntityActionSnackbar, mutate, track],
  );

  const handleConfirmDuplicate = useCallback(async () => {
    if (!pendingInvoiceData) return;
    // `|| undefined` (not `??`) so empty-string ids drop the field
    // entirely instead of polluting the event with `invoice_id: ''`.
    track('invoice_duplicate_warning_overridden', {
      invoice_id: pendingInvoiceData.id || undefined,
    });
    setPendingInvoiceData(null);
    try {
      await finishSave(pendingInvoiceData, true);
    } catch (err) {
      console.error(err);
      showSnackbar(formatLiftAPIClientError('Invoice failed to save', err), { variant: 'error' });
    }
  }, [finishSave, pendingInvoiceData, showSnackbar, track]);

  const onValidSubmit = useCallback(
    async (data: InvoiceDto) => {
      const buildInvoiceData = () => {
        const invoiceData = invoiceFormSchema.parse(data);
        // parentInvoiceId & adjustmentSource have no rendered form inputs, so
        // react-hook-form's handleSubmit does not include them in `data`.
        // Pull them from the initialData prop instead.
        if (initialData?.parentInvoiceId) {
          invoiceData.parentInvoiceId = initialData.parentInvoiceId;
        }
        if (initialData?.adjustmentSource) {
          invoiceData.adjustmentSource = initialData.adjustmentSource;
        }
        return applyCreditRules(invoiceData);
      };

      try {
        await finishSave(buildInvoiceData());
      } catch (err) {
        if (
          err instanceof LiftAPIClientError &&
          err.context.status === 409 &&
          (err.context.data as { code?: string })?.code === 'DUPLICATE_INVOICE'
        ) {
          const pending = buildInvoiceData();
          track('invoice_duplicate_warning_shown', {
            invoice_id: pending.id || undefined,
          });
          setPendingInvoiceData(pending);
          return;
        }
        console.error(err);
        showSnackbar(formatLiftAPIClientError('Invoice failed to save', err), {
          variant: 'error',
        });
      }
    },
    [finishSave, initialData, showSnackbar, track],
  );

  return (
    <>
      <form
        noValidate
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        onSubmit={handleSubmit(
          onValidSubmit,
          createFormInvalidHandler<InvoiceDtoValues>({ formName: 'InvoiceForm', showSnackbar }),
        )}
      >
        {children}
      </form>
      <LAIConfirmationDialog
        open={pendingInvoiceData !== null}
        title="Duplicate Invoice Detected"
        onAccept={handleConfirmDuplicate}
        onClose={() => setPendingInvoiceData(null)}
        onCancel={() => setPendingInvoiceData(null)}
        acceptButtonText="Proceed Anyway"
        cancelButtonText="Cancel"
      >
        An invoice with these exact details already exists in the system. Are you sure you want to
        proceed?
      </LAIConfirmationDialog>
    </>
  );
};

/** Marks the invoice as an adjustment of an original on the same property and vendor. */
const AdjustmentFields: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { control, setValue } = useFormContext<InvoiceDtoValues>();
  const isAdjustment = useWatch({ control, name: 'isAdjustment' }) ?? false;
  const propertyId = useWatch({ control, name: 'propertyId' });
  const serviceProviderId = useWatch({ control, name: 'serviceProviderId' });
  const parentInvoiceId = useWatch({ control, name: 'parentInvoiceId' });

  const { data: options, isLoading } = useSWR(
    isAdjustment && propertyId
      ? (['invoiceAdjustmentOriginals', propertyId, serviceProviderId ?? ''] as const)
      : null,
    originalInvoiceOptionsFetcher,
  );

  // A property or vendor change can leave the picked original out of the list.
  useEffect(() => {
    if (options && parentInvoiceId && !options.some((o) => o.id === parentInvoiceId)) {
      setValue('parentInvoiceId', undefined, { shouldValidate: true });
    }
  }, [options, parentInvoiceId, setValue]);

  return (
    <Grid container spacing={2} my={2}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControlLabel
          label="Adjusted invoice"
          disabled={disabled}
          control={
            <Switch
              checked={isAdjustment}
              onChange={(_, checked) => {
                setValue('isAdjustment', checked);
                if (!checked) setValue('parentInvoiceId', undefined);
              }}
            />
          }
        />
      </Grid>
      {isAdjustment ? (
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="parentInvoiceId"
            control={control}
            render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                value={options?.find((o) => o.id === value) ?? null}
                onBlur={onBlur}
                onChange={(_, option) => onChange(option?.id ?? undefined)}
                options={options ?? []}
                getOptionLabel={(option) => `#${option.number}`}
                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                loading={isLoading}
                disabled={disabled || !propertyId}
                noOptionsText="No invoices for this property and vendor"
                size="small"
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Original invoice"
                    required
                    error={error !== undefined}
                    helperText={
                      error?.message ?? (propertyId ? undefined : 'Select a property first')
                    }
                  />
                )}
              />
            )}
          />
        </Grid>
      ) : null}
    </Grid>
  );
};

const InvoiceFields: React.FC<{
  disabled?: boolean;
  allowAdjustment?: boolean;
  showKind?: boolean;
}> = ({ disabled, allowAdjustment, showKind } = {}) => {
  const { userGroup } = useContext(UserContext);
  const { control } = useFormContext<InvoiceDtoValues>();
  const terms = useLocaleTerms();
  const propertyId = useWatch({ control, name: 'propertyId' });
  const kind = useWatch({ control, name: 'kind' });
  const fetcher = useSWR(
    userGroup ? ['InvoiceForm.Options', userGroup] : null,
    invoiceOptionsFetcher,
  );
  const data = fetcher.data;

  const { cars } = usePropertyCars(propertyId);
  const { data: serviceProviders } = useSWR(
    propertyId ? ['invoiceServiceProviders', propertyId] : null,
    invoiceServiceProvidersFetcher,
    {},
  );
  const fields = useMemo(() => {
    const defs = buildFieldDef({
      properties: data?.properties,
      consultants: data?.consultants,
      kind,
      cars,
      serviceProviders,
      terms,
      showKind,
    });
    if (!disabled) return defs;
    const disableFields = (fields: typeof defs.generalFields) =>
      fields.map((f) => ({ ...f, disabled: true }));
    return {
      generalFields: disableFields(defs.generalFields),
      itemsFields: disableFields(defs.itemsFields),
      totalField: { ...defs.totalField, disabled: true },
    };
  }, [
    data?.properties,
    data?.consultants,
    kind,
    cars,
    serviceProviders,
    disabled,
    terms,
    showKind,
  ]);

  const { generalFields, itemsFields, totalField } = fields;

  return (
    <>
      <Typography variant="h6" my={2}>
        General
      </Typography>
      <Grid container spacing={2} my={2}>
        {generalFields.map((field) => (
          <FieldAndCell key={field.name} field={field} control={control} />
        ))}
      </Grid>
      {kind === InvoiceKind.Credit ? <CreditFields disabled={disabled} /> : null}
      {allowAdjustment && kind !== InvoiceKind.Credit ? (
        <AdjustmentFields disabled={disabled} />
      ) : null}
      <Box my={2}>
        <Typography variant="h6" py={2}>
          Items
        </Typography>
        <Grid container spacing={2} py={2}>
          {itemsFields.map((field) => (
            <FieldAndCell key={field.name} field={field} control={control} />
          ))}
        </Grid>
        <Grid container spacing={2} py={2}>
          <FieldAndCell field={totalField} control={control} />
        </Grid>
      </Box>
    </>
  );
};

const InvoiceFieldsWithKind: React.FC<{ disabled?: boolean; allowAdjustment?: boolean }> = (
  props,
) => <InvoiceFields {...props} showKind />;
